// Canonical, INDUSTRY-AGNOSTIC question archetype emitted by the planner LLM.
//
// This is the structured signal the runtime routes on. The planner resolves the
// user's intent from full context (WorkspaceContextFrame + ConversationContextFrame
// + governed evidence posture) and emits one of these classes. The runtime then
// validates the structured plan against capability manifests + evidence posture and
// selects the answer path from `intentClass` — NEVER by matching industry vocabulary
// ("carrier", "buy-rate", "delayering", "cloud COGS") in the raw question string.
//
// Industry-specific phrasing belongs in planner-prompt overlays + capability
// descriptions, so the same question routes identically across industries.
//
// Defined in its own zero-dependency contract module so both `planner-decision.ts`
// and `governed-capability-manifest.ts` can reference it without a circular import.
export const PLANNER_INTENT_CLASSES = [
  // Explain / decompose ONE governed number: "where does X come from",
  // "what is the build-up of X", "how is X calculated". Single-module drill-down;
  // must NEVER be swallowed into a composite value-gap bridge.
  'provenance_drilldown',
  // Read a governed metric / segment as-is: "what's our operating income",
  // "segment margin by lane". A direct readout, not a multi-lever bridge.
  'metric_readout',
  // Close / lift an objective to a target across multiple levers — the composite
  // operating-income / EBITDA bridge. Requires a finance value-gap outcome plan.
  // AUTHORITY, not capability: this class alone admits the composite canvas that
  // prints the OBJECTIVE, the REQUIRED UPLIFT and the VERDICT. Only the objective
  // owner declares it. See `value_gap_contribution` immediately below.
  'value_gap_bridge',
  // #4008 FWF — "size MY contribution to the objective bridge". The class the
  // ~24 module `*_size_contribution` / `*_contribution_bridge` intents actually
  // mean when they say they serve the bridge.
  //
  // WHY IT EXISTS. Before the split one class string carried two meanings —
  // "this module can CONTRIBUTE to the objective bridge" and "this turn IS the
  // objective bridge" — and the runtime read the wider one. 24 module intents
  // across 18 modules were therefore each a door into a canvas that prints one
  // fixed finance objective; FWD captured four rows from four different modules
  // minting one specId, one baseline and one verdict
  // (`.codex/reports/4008-fwd/`).
  //
  // A contribution turn is its OWN module's lane: it sizes that module's
  // contribution with its own readout. The composite bridge still CALLS it for
  // that sized contribution — it simply never BECOMES it.
  //
  // ORDERING IS LOAD-BEARING. `PLANNER_INTENT_CLASSES.find(...)` picks the first
  // class an intent serves, so this member sits AFTER `value_gap_bridge`
  // deliberately: an intent that somehow served both still resolves as the
  // bridge rather than silently changing behaviour under the reader.
  'value_gap_contribution',
  // Run a gated scenario / simulation / optimization (e.g. workforce reduction,
  // a dollar-target solve). Subject to scenario authorization.
  'scenario_plan',
  // Company / strategy-pack readback with no governed compute.
  'context_readback',
  // "What evidence is missing / what's needed for X" — a META question ABOUT the
  // governed evidence state, not a request for a governed number. It is answered
  // by ENUMERATING the named evidence gaps as a reporting surface (no numeric
  // claims), so the value fence does not gate it. Never carries numbers.
  'evidence_status',
  // Board / executive composite package over levers + gaps.
  'decision_package',
  // Not a Helix workspace question (sports, weather, market prices, trivia).
  'out_of_scope',
  // The planner could not confidently classify the turn. The runtime falls back to
  // structural derivation (planned-call shape) and answers or blocks honestly.
  'unresolved',
] as const;

export type PlannerIntentClass = (typeof PLANNER_INTENT_CLASSES)[number];
// Observe-only routing archetype emitted only when ASK_HELIX_ROUTING_SHADOW is
// enabled. This is deliberately separate from `PlannerIntentClass` while shadow
// mode is collecting agreement telemetry; legacy routing remains authoritative.
export const PLANNER_ARCHETYPES = [
  'value_gap_bridge',
  'long_range_plan',
  'drift_realization',
  'situation_rollup',
  'review_pack',
  'variance_decomposition',
  'scenario_chain',
  'trajectory_forecast',
  'tradeoff',
  'metric_readout',
  'scenario_plan',
  'provenance_drilldown',
  'decision_package',
  'out_of_universe',
] as const;

export type PlannerArchetype = (typeof PLANNER_ARCHETYPES)[number];

export function isPlannerArchetype(value: unknown): value is PlannerArchetype {
  return (
    typeof value === 'string' &&
    (PLANNER_ARCHETYPES as readonly string[]).includes(value)
  );
}

export function isPlannerIntentClass(value: unknown): value is PlannerIntentClass {
  return (
    typeof value === 'string' &&
    (PLANNER_INTENT_CLASSES as readonly string[]).includes(value)
  );
}
